import { RecurrenceUnit } from '@/types/graphql';
import { IOrderFrequency, isSingletonFrequency } from '@/types/order';
import {
    renderBaseInterval,
    renderRecurrenceUnit,
    renderRecurrenceUnitAdverb,
    renderRecurrenceUnitCompact,
} from '@/types/recurrence';

// Whether a PRN order can take another active visit.
export const orderHasRoom = (order: {
    numActiveOccurrences: number;
    maxActiveOccurrences: number;
}): boolean => order.numActiveOccurrences < order.maxActiveOccurrences;

// "Week" / "Day" / "Month". The noun for a single interval, by frequency unit.
export const unitNoun = (unit: RecurrenceUnit): string => {
    switch (unit) {
        case RecurrenceUnit.DAY:
        case RecurrenceUnit.EVERY_OTHER_DAY:
            return 'Day';
        case RecurrenceUnit.WEEK:
        case RecurrenceUnit.EVERY_OTHER_WEEK:
            return 'Week';
        case RecurrenceUnit.MONTH:
        case RecurrenceUnit.EVERY_OTHER_MONTH:
            return 'Month';
        default:
            return 'Interval';
    }
};

// How often an interval comes round: "Weekly" when an interval is one base unit, "Every 4
// Weeks" when it is wider.
function renderFrequencyCadence(frequency: IOrderFrequency): string {
    const { intervalUnit, unitsPerInterval } = frequency;
    if (unitsPerInterval === 1) {
        return renderRecurrenceUnitAdverb(intervalUnit);
    }
    return `Every ${unitsPerInterval} ${renderRecurrenceUnit(intervalUnit, unitsPerInterval)}`;
}

export function renderOrderFrequencyOnceTwiceStyle({
    frequency,
    withIntervals,
}: {
    frequency: IOrderFrequency;
    withIntervals: boolean;
}) {
    if (isSingletonFrequency(frequency)) {
        return 'Once';
    }
    const { numVisitsPerInterval, intervalCount, intervalUnit } = frequency;
    const cadence = renderFrequencyCadence(frequency);
    let label = '';
    switch (numVisitsPerInterval) {
        case 1:
            label = `Once ${cadence}`;
            break;
        case 2:
            label = `Twice ${cadence}`;
            break;
        default:
            label = `${numVisitsPerInterval}x ${cadence}`;
            break;
    }
    if (withIntervals) {
        label += ` for ${intervalCount} ${renderRecurrenceUnit(intervalUnit, intervalCount)}`;
    }
    return label;
}

export function renderOrderFrequency1xStyle({
    frequency,
    withIntervals,
}: {
    frequency: IOrderFrequency;
    withIntervals: boolean;
}) {
    const { numVisitsPerInterval, intervalCount, intervalUnit } = frequency;
    let label = `${numVisitsPerInterval}x ${renderFrequencyCadence(frequency)}`;

    if (withIntervals) {
        label += ` for ${intervalCount} ${renderBaseInterval(intervalUnit, intervalCount)}`;
    }

    return label.toLowerCase();
}

// The shorthand form, for places too narrow for a sentence: "2w3" reads as twice weekly for
// three weeks. The unit always shows, because "2d3" and "2m3" are equally valid orders. An
// interval wider than one unit needs the interval itself, so it reads "2/3w × 6w": twice
// every three weeks, over six weeks.
export function renderOrderFrequencyCompact({ frequency }: { frequency: IOrderFrequency }) {
    const { numVisitsPerInterval, intervalCount, intervalUnit, unitsPerInterval } = frequency;
    const unit = renderRecurrenceUnitCompact(intervalUnit);

    if (unitsPerInterval === 1) {
        return `${numVisitsPerInterval}${unit}${intervalCount}`;
    }
    return `${numVisitsPerInterval}/${unitsPerInterval}${unit} × ${intervalCount}${unit}`;
}

export function renderOrderMinMaxActiveOccurrences({
    numActiveOccurrences,
    maxActiveOccurrences,
    minActiveOccurrences,
}: {
    numActiveOccurrences: number;
    maxActiveOccurrences: number;
    minActiveOccurrences: number;
}) {
    if (minActiveOccurrences === maxActiveOccurrences) {
        return `${numActiveOccurrences} / ${minActiveOccurrences} visits`;
    }
    return `${numActiveOccurrences} / ${minActiveOccurrences} - ${maxActiveOccurrences} visits`;
}
